import React, { useEffect, useMemo, useRef } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import {
  MapContainer,
  Marker,
  useMapEvents,
  ZoomControl,
  TileLayer,
} from "react-leaflet";
import { DivIcon, LatLngBounds, Map as LeafletMap } from "leaflet";

import { Park, ParkRide } from "../types";
import { markerColorMap, markerIconMap } from "../consts";

const Coordinates = () => {
  useMapEvents({
    click(e) {
      console.log([e.latlng.lat, e.latlng.lng]);
    },
  });
  return <></>;
};

const Map = (props: {
  parks: Park[];
  rides: ParkRide[];
  selectedRide?: string;
  selectRide: (name: string) => void;
}) => {
  const windowWidth = window.innerWidth;
  const { parks, rides, selectedRide, selectRide } = props;
  const map = useRef<LeafletMap>(null);

  const markers = useMemo(() => {
    return rides.map((ride) => {
      const isSelected = selectedRide === ride.title;
      return (
        <Marker
          key={ride.title}
          icon={
            new DivIcon({
              html: renderToStaticMarkup(
                <div
                  className="rounded-full flex items-center justify-center"
                  style={{
                    backgroundColor: markerColorMap.get(ride.type),
                    width: isSelected ? 28 : 18,
                    height: isSelected ? 28 : 18,
                  }}
                >
                  {markerIconMap.get(ride.type)?.(isSelected)}
                </div>,
              ),
              iconSize: isSelected ? [28, 28] : [18, 18],
              iconAnchor: isSelected ? [14, 14] : [9, 9],
            })
          }
          opacity={isSelected ? 1 : selectedRide ? 0.6 : 1}
          position={ride.geo}
          eventHandlers={{
            click: () => {
              selectRide(ride.title);
              map.current?.flyTo(
                [
                  ride.geo[0] - (windowWidth < 640 ? 0.0004 : 0),
                  ride.geo[1] - (windowWidth < 640 ? 0 : 0.0005),
                ],
                19,
              );
            },
          }}
        />
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rides, selectRide, selectedRide]);

  useEffect(() => {
    if (!map.current || parks.length === 0) return;

    if (parks.length === 1) {
      map.current.flyTo(parks[0].center, 17);
    } else {
      const allRides = parks.flatMap((p) => p.rides);
      if (allRides.length === 0) return;
      const bounds = new LatLngBounds(allRides.map((r) => r.geo));
      map.current.flyToBounds(bounds, { padding: [50, 50] });
    }
  }, [parks]);

  const initialCenter = parks[0]?.center ?? [33.81209, -117.91895];

  return (
    <MapContainer
      ref={map}
      className="w-screen h-[100dvh] z-0"
      center={initialCenter}
      zoom={17}
      minZoom={14}
      maxZoom={20}
      attributionControl={false}
      zoomControl={false}
    >
      <ZoomControl position="topright" />
      {markers}
      <TileLayer
        maxZoom={20}
        minZoom={14}
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png"
        className="grayscale"
      />
      <Coordinates />
    </MapContainer>
  );
};

export default Map;
